// project/desktop-web/src/pages/pickup/pickup-task/hooks/usePickupTaskInternationalization.ts

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    PickupTaskStatus, 
    PickupTaskStartPointType, 
    PickupTaskEndPointType 
} from '../types/PickupTaskType';

export const usePickupTaskInternationalization = () => {
    const { t } = useTranslation();

    // 🌍 任务状态国际化
    const getTaskStatusText = (status: PickupTaskStatus): string => {
        switch (status) {
            case PickupTaskStatus.DRAFT: 
                return t('enum.pickupTaskStatus.draft');
            case PickupTaskStatus.PUBLISHED: 
                return t('enum.pickupTaskStatus.published');
            case PickupTaskStatus.IN_PROGRESS: 
                return t('enum.pickupTaskStatus.inProgress');
            case PickupTaskStatus.COMPLETED: 
                return t('enum.pickupTaskStatus.completed');
            case PickupTaskStatus.CANCELLED: 
                return t('enum.pickupTaskStatus.cancelled');
            default: 
                return status;
        }
    };

    // 🌍 任务状态选项
    const taskStatusOptions = useMemo(() => {
        return Object.values(PickupTaskStatus).map(status => ({
            value: status,
            label: getTaskStatusText(status)
        }));
    }, [t]);

    // 🌍 起点类型国际化
    const getStartPointTypeText = (type: PickupTaskStartPointType): string => {
        switch (type) {
            case PickupTaskStartPointType.HUB:
                return t('enum.pickupPointType.hub');
            case PickupTaskStartPointType.RALLY_POINT:
                return t('enum.pickupPointType.rallyPoint');
            case PickupTaskStartPointType.FIRST_APPOINTMENT:
                return t('enum.pickupPointType.firstAppointment');
            case PickupTaskStartPointType.WAREHOUSE:
                return t('enum.pickupPointType.warehouse');
            default:
                return t('enum.pickupPointType.unknown');
        }
    };

    // 🌍 终点类型国际化
    const getEndPointTypeText = (type: PickupTaskEndPointType): string => {
        switch (type) {
            case PickupTaskEndPointType.HUB:
                return t('enum.pickupPointType.hub');
            case PickupTaskEndPointType.RALLY_POINT:
                return t('enum.pickupPointType.rallyPoint');
            default:
                return t('enum.pickupPointType.unknown');
        }
    };

    // 🌍 起点类型选项
    const startPointTypeOptions = useMemo(() => [
        { label: getStartPointTypeText(PickupTaskStartPointType.HUB), value: PickupTaskStartPointType.HUB },
        { label: getStartPointTypeText(PickupTaskStartPointType.RALLY_POINT), value: PickupTaskStartPointType.RALLY_POINT },
        { label: getStartPointTypeText(PickupTaskStartPointType.FIRST_APPOINTMENT), value: PickupTaskStartPointType.FIRST_APPOINTMENT },
        { label: getStartPointTypeText(PickupTaskStartPointType.WAREHOUSE), value: PickupTaskStartPointType.WAREHOUSE }
    ], [t]);

    // 🌍 终点类型选项
    const endPointTypeOptions = useMemo(() => [
        { label: getEndPointTypeText(PickupTaskEndPointType.HUB), value: PickupTaskEndPointType.HUB },
        { label: getEndPointTypeText(PickupTaskEndPointType.RALLY_POINT), value: PickupTaskEndPointType.RALLY_POINT }
    ], [t]);

    return {
        getTaskStatusText,
        taskStatusOptions,
        getStartPointTypeText,
        getEndPointTypeText,
        startPointTypeOptions,
        endPointTypeOptions
    };
};
